import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import { SideDrawerRef } from '../../../SideDrawer/SideDrawer.component';
import { MoveDocument } from '../../components/DataRoomDocumentDrawers/MoveDocument';
import { MoveDocumentRef } from '../../components/DataRoomDocumentDrawers/MoveDocument/MoveDocument.component';
import { RenameModal } from '../../components/DataRoomDocumentDrawers/RenameModal';
import ManageFileAccess from '../../components/DataRoomDocumentDrawers/ManageFileAccess';
import DrNodeTagDrawer from '../../components/DrNodeTagDrawer';
import DataRoomDocumentsContext from '../DataRoomDocuments/DataRoomDocuments.context';
import DataRoomSelectedDocumentsContext from '../DataRoomSelectedDocuments/DataRoomSelectedDocuments.context';
import DataRoomNodeActionsContext from './DataRoomNodeActions.context';

import './DataRoomNodeActions.styles.scss';
import { getArrayLength, getHasElementsInArray } from 'utils';

/**
 * Owns the one instance of each node action drawer for the Files page.
 *
 * Every row used to render its own Rename / Move / Manage access / Tag drawer,
 * so a folder with a few hundred nodes mounted a few hundred of each — the main
 * reason the page froze. The drawers now live here and are pointed at whichever
 * nodes the caller passes in, which also means they survive a row unmounting
 * (a prerequisite for virtualising the list).
 */
const DataRoomNodeActionsProvider: React.FC = ({ children }) => {
  const [actionTargets, setActionTargets] = useState<DataRoomNode[]>([]);

  const { setDataRoom } = useContext(DataRoomContext);
  const { currentlyDisplayedNodes, setCurrentlyDisplayedNodes } = useContext(
    DataRoomDocumentsContext,
  );
  const { selected, updateSelectedValue } = useContext(
    DataRoomSelectedDocumentsContext,
  );

  const liveActionNodes = useRef<DataRoomNode[]>([]);

  /**
   * The stored targets are only a starting point — a drawer has to see edits
   * made to its nodes while it is open (tagging a file, for instance, updates
   * the list and the drawer has to follow), so they are re-read from the live
   * list on every render. A target that has left the folder (just moved, say)
   * falls back to the stored copy so the closing drawer keeps its contents.
   */
  const actionNodes = useMemo(() => {
    const liveById = new Map(
      currentlyDisplayedNodes.map((node) => [node._id, node]),
    );

    const next = actionTargets.map(
      (target) => liveById.get(target._id) ?? target,
    );

    // Hold the array identity steady while the nodes themselves are unchanged,
    // so an unrelated list update does not read as a new set of nodes (which
    // would, for one, make ManageFileAccess refetch).
    const previous = liveActionNodes.current;

    if (
      getArrayLength(previous) === getArrayLength(next) &&
      previous.every((node, index) => node === next[index])
    ) {
      return previous;
    }

    liveActionNodes.current = next;

    return next;
  }, [actionTargets, currentlyDisplayedNodes]);

  const { documentId } = useParams<{ documentId: string }>();

  const [renameModal, renameModalRef] = useCallbackRef<ModalRef | null>(null);
  const [moveFiles, moveFilesRef] = useCallbackRef<MoveDocumentRef | null>(
    null,
  );
  const [manageAccess, manageAccessRef] = useCallbackRef<SideDrawerRef | null>(
    null,
  );
  const [tagDrawer, tagDrawerRef] = useCallbackRef<SideDrawerRef | null>(null);

  // Pointing a drawer at new nodes and opening it are both state updates, so
  // React batches them and the drawer opens already showing the right nodes.
  const openRename = useCallback(
    (nodes: DataRoomNode[]) => {
      setActionTargets(nodes);
      renameModal?.open();
    },
    [renameModal],
  );

  const openMove = useCallback(
    (nodes: DataRoomNode[]) => {
      setActionTargets(nodes);
      moveFiles?.open();
    },
    [moveFiles],
  );

  const openManageAccess = useCallback(
    (nodes: DataRoomNode[]) => {
      setActionTargets(nodes);
      manageAccess?.setOpen(true);
    },
    [manageAccess],
  );

  const openTag = useCallback(
    (node: DataRoomNode) => {
      setActionTargets([node]);
      tagDrawer?.setOpen(true);
    },
    [tagDrawer],
  );

  const onTagSet = useCallback(
    (node: DataRoomNode) => {
      setCurrentlyDisplayedNodes((old) =>
        old.map((el) => (el._id === node._id ? node : el)),
      );
      setDataRoom(({ existTags, ...rest }) => ({
        ...rest,
        existTags: [...existTags.filter((tag) => tag === node.tag), node.tag],
      }));

      if (getHasElementsInArray(selected)) updateSelectedValue(node);
    },
    [selected, setCurrentlyDisplayedNodes, setDataRoom, updateSelectedValue],
  );

  const removeTagFromOldNode = useCallback(
    (nodeId: string) => {
      setCurrentlyDisplayedNodes((old) =>
        old.map((el) => (el._id === nodeId ? { ...el, tag: null } : el)),
      );
    },
    [setCurrentlyDisplayedNodes],
  );

  const updateNodes = useCallback(
    (dataRoomNodes: DataRoomNode[]) => {
      setCurrentlyDisplayedNodes((prev) =>
        prev.map((el) => {
          const foundNode = dataRoomNodes.find(({ _id }) => _id === el._id);

          return foundNode ? { ...el, ...foundNode } : el;
        }),
      );
    },
    [setCurrentlyDisplayedNodes],
  );

  const isAnyActionOpen = !!(
    renameModal?.openState ||
    moveFiles?.isOpen ||
    manageAccess?.open ||
    tagDrawer?.open
  );

  const value = useMemo(
    () => ({
      actionNodes,
      isAnyActionOpen,
      openRename,
      openMove,
      openManageAccess,
      openTag,
      onTagSet,
      removeTagFromOldNode,
    }),
    [
      actionNodes,
      isAnyActionOpen,
      openRename,
      openMove,
      openManageAccess,
      openTag,
      onTagSet,
      removeTagFromOldNode,
    ],
  );

  return (
    <DataRoomNodeActionsContext.Provider value={value}>
      {children}

      <div className="data-room-node-actions">
        <MoveDocument nodes={actionNodes} ref={moveFilesRef} />

        <ManageFileAccess
          ref={manageAccessRef}
          nodes={actionNodes}
          parentId={actionNodes[0]?.parentId ?? documentId}
          updateNodes={updateNodes}
        />

        <DrNodeTagDrawer
          ref={tagDrawerRef}
          node={actionNodes[0]}
          onSuccessTag={onTagSet}
          removeTagFromOldNode={removeTagFromOldNode}
        />

        <RenameModal node={actionNodes[0]} ref={renameModalRef} />
      </div>
    </DataRoomNodeActionsContext.Provider>
  );
};

export default DataRoomNodeActionsProvider;
